import { MouseEventHandler } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Typography } from "@mui/material";

import { MediaFullScreen } from "../../../common/components/media/MediaFullScreen";
import { EventTableColumn } from "../components/table/EventTable";
import FeedbackDetailsTableCellContainer from "../components/feedback/FeedbackDetailsTableCellContainer";
import FeedbackThumbsTableCellContainer from "../components/feedback/FeedbackThumbsTableCellContainer";
import { EventViewModel } from "../models/eventViewModel";
import { RecordingViewModel } from "../models/recordingViewModel";
import { useUpdateEventFeedback } from "./events/useUpdateEventFeedback";
import { formatTimestamp } from "../../../common/utils/timestampUtils";
import TableCellTruncated from "../../../common/components/table/TableCellTruncated";

export const useEventLeakTableColumns = (
  newestRecording?: RecordingViewModel,
  onMoreActionsClick?: MouseEventHandler<HTMLButtonElement>
) => {
  const { updateEventFeedback } = useUpdateEventFeedback();

  const columns: EventTableColumn<EventViewModel>[] = [
    {
      title: "Event",
      width: "22%",
      content: event => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.75em",
            cursor: "pointer",
            position: "relative",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Box sx={{ width: 70, height: 40 }}>
              <MediaFullScreen
                keyFrame={newestRecording?.keyFrame}
                mediaOutput={newestRecording?.mediaOutput}
                s3ImageFullScreenProps={{
                  loadingFallback: (
                    <Box
                      sx={{
                        backgroundColor: theme =>
                          theme.palette.otherOutlineBorder.main,
                      }}
                      width="100%"
                      height={40}
                    />
                  ),
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="buttonMedium">{event.modelId}</Typography>
            <Typography variant="body2Regular" color="text.secondary">
              {formatTimestamp(event.timestamp)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      title: "Feedback",
      content: event => {
        return (
          <FeedbackThumbsTableCellContainer
            event={event}
            onUpdateEventFeedback={updateEventFeedback}
          />
        );
      },
    },
    {
      title: "Feedback details",
      content: event => {
        return (
          <FeedbackDetailsTableCellContainer
            event={event}
            onUpdateEventFeedback={updateEventFeedback}
          />
        );
      },
    },
    {
      title: "Equipment classification",
      content: event => (
        <TableCellTruncated
          clickableCursor
          tooltipText={event.equipmentClassification}
          text={event.equipmentClassification}
          TypographyProps={{ variant: "body2" }}
        />
      ),
    },
    {
      title: "Source",
      content: event => (
        <TableCellTruncated
          clickableCursor
          tooltipText={event.eventSource}
          text={event.eventSource}
          TypographyProps={{ variant: "body2" }}
        />
      ),
    },
    {
      title: "Emission Type",
      content: event => (
        <TableCellTruncated
          clickableCursor
          tooltipText={event.emissionType}
          text={event.emissionType}
          TypographyProps={{ variant: "body2" }}
        />
      ),
    },
    {
      title: "Summary",
      content: event => (
        <TableCellTruncated
          clickableCursor
          tooltipText={event.summary}
          text={event.summary}
          TypographyProps={{ variant: "body2" }}
        />
      ),
    },
    {
      title: "",
      width: "40px",
      content: () => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            height: "100%",
          }}
        >
          <IconButton aria-label="more actions" onClick={onMoreActionsClick}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return { columns };
};
